<script setup lang="ts" generic="D">
import { type ColumnDef, FlexRender, getCoreRowModel, useVueTable } from '@tanstack/vue-table';

const props = defineProps<{
    data: D[];
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    columns: ColumnDef<D, any>[];
    isLoading?: boolean;
    skeleton: {
        rows: number;
        height: number;
    };
}>();

defineOptions({
    inheritAttrs: false
});

const table = useVueTable({
    columns: props.columns,
    get data() {
        return props.data;
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true
});

const hasData = computed(() => props.data.length > 0);

const skeletonRows = computed(() => {
    return hasData.value ? props.data.length : props.skeleton.rows;
});

</script>

<template>
  <div v-bind="$attrs"
       class="table-container"
  >
    <table v-if="isLoading || hasData"
           class="width-full table"
    >
      <thead>
        <tr v-for="headerGroup in table.getHeaderGroups()"
            :key="headerGroup.id"
        >
          <th v-for="header in headerGroup.headers"
              :key="header.id"
              class="compact-400-accent color-head-table"
              :colSpan="header.colSpan"
          >
            <FlexRender v-if="!header.isPlaceholder"
                        :render="header.column.columnDef.header"
                        :props="header.getContext()"
            />
          </th>
        </tr>
      </thead>
      <Transition mode="out-in"
                  name="fade"
      >
        <tbody key="loading"
               v-if="isLoading"
        >
          <tr v-for="index in skeletonRows"
              :key="'skeleton-' + index"
          >
            <td v-for="column in props.columns"
                :key="'skeleton-cell-' + column.id"
            >
              <LoaderSkeleton class="grow"
                              :height="props.skeleton.height"
              />
            </td>
          </tr>
        </tbody>
        <tbody key="table"
               v-else-if="hasData"
        >
          <tr v-for="row in table.getRowModel().rows"
              :key="row.id"
          >
            <td v-for="cell in row.getVisibleCells()"
                :key="cell.id"
            >
              <FlexRender :render="cell.column.columnDef.cell"
                          :props="cell.getContext()"
              />
            </td>
          </tr>
        </tbody>
      </Transition>
    </table>
    <div v-else
         class="text-center no-data-row height-full display-flex justify-center align-items-center grow"
    >
      <slot name="no-data">No data available</slot>
    </div>
  </div>
</template>

<style scoped lang="scss">
.table-container {
  flex-grow: 1;
  overflow: auto hidden;

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    border-radius: 100px;
    background: var(--layer-floor-2);
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 100px;
    background-color: var(--bg-primary);
  }
}

.table {
  width: 100%;
  border-collapse: collapse;

  tr,
  td {
    vertical-align: middle;
  }

  th,
  td {
    padding: 20px 8px;
  }

  th {
    text-align: left;
  }

  td:nth-child(n + 3),
  th:nth-child(n + 3) {
    box-sizing: border-box;
  }

  td:nth-child(n + 3):nth-child(-n + 8) {
    padding-right: 20px;
  }
}

.color-head-table {
  color: var(--content-secondary);

  div {
    font-family: "Space Grotesk", sans-serif;
    font-size: 14px;
    font-weight: 600;
  }
}

.no-data-row {
  padding: 20px;
  color: var(--content-secondary);
  font-size: 16px;
  font-weight: 500;
  text-align: center;
}

.color-change-red {
  color: var(--content-negative);
}

.color-change-green {
  color: var(--content-brand-mojito);
}

.color-change-neutral {
  color: $palette-goose-0000;
}

.svg-logo-size {
  width: 16px;
  height: 16px;
}

:deep(.table-img-collect) {
  width: 36px;
  height: 36px;
  border: var(--image-paranja-stroke, 1px) solid var(--border-paranja, rgb(255 255 255 / 20%));
  border-radius: var(--control-400-radius, 2px);
}

:deep(.table-title-ico) {
  width: 16px;
  min-width: 16px;
}

:deep(.token-logo) {
  width: 24px;
  height: 24px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  transform: translateY(3px);
  opacity: 0;
}
</style>
